import { create } from 'zustand'
import { supabase } from '../lib/supabase'
import { useAuthStore } from './authStore'

interface UserProfile {
  id: string
  email: string
  role: 'admin' | 'trainer'
  status: 'pending' | 'approved' | 'rejected'
  name: string | null
  team: string | null
  created_at: string
  approved_by: string | null
  approved_at: string | null
}

interface UserManagementStore {
  users: UserProfile[]
  loading: boolean
  error: string | null
  
  // Actions
  fetchUsers: () => Promise<void>
  approveUser: (userId: string) => Promise<{ error?: string }>
  rejectUser: (userId: string) => Promise<{ error?: string }>
  changeUserRole: (userId: string, role: 'admin' | 'trainer') => Promise<{ error?: string }>
  deleteUser: (userId: string) => Promise<{ error?: string }>
  
  // Getters
  getPendingUsers: () => UserProfile[]
  getApprovedUsers: () => UserProfile[]
  getAdminEmails: () => string[]
  getRejectedUsers: () => UserProfile[]
}

export const useUserManagementStore = create<UserManagementStore>((set, get) => ({
  users: [],
  loading: false,
  error: null,

  fetchUsers: async () => {
    set({ loading: true, error: null })
    try {
      const { profile } = useAuthStore.getState()
      if (!profile || profile.role !== 'admin') {
        throw new Error('Nur Administratoren können Benutzer verwalten')
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error

      set({ users: data || [], loading: false })
    } catch (error: any) {
      set({ error: error.message, loading: false })
    }
  },

  approveUser: async (userId: string) => {
    try {
      const { profile } = useAuthStore.getState()
      if (!profile || profile.role !== 'admin') {
        return { error: 'Nur Administratoren können Benutzer genehmigen' }
      }

      const { error } = await supabase
        .from('profiles')
        .update({
          status: 'approved',
          approved_by: profile.id,
          approved_at: new Date().toISOString(),
        })
        .eq('id', userId)

      if (error) throw error

      // Update local state
      const users = get().users.map(user => 
        user.id === userId 
          ? { 
              ...user, 
              status: 'approved' as const,
              approved_by: profile.id,
              approved_at: new Date().toISOString()
            }
          : user
      )
      set({ users })

      return {}
    } catch (error: any) {
      return { error: error.message }
    }
  },

  rejectUser: async (userId: string) => {
    try {
      const { profile } = useAuthStore.getState()
      if (!profile || profile.role !== 'admin') {
        return { error: 'Nur Administratoren können Benutzer ablehnen' }
      }

      const { error } = await supabase
        .from('profiles')
        .update({
          status: 'rejected',
          approved_by: profile.id,
          approved_at: new Date().toISOString(),
        })
        .eq('id', userId)

      if (error) throw error

      // Update local state
      const users = get().users.map(user => 
        user.id === userId 
          ? { 
              ...user, 
              status: 'rejected' as const,
              approved_by: profile.id,
              approved_at: new Date().toISOString()
            }
          : user
      )
      set({ users })

      return {}
    } catch (error: any) {
      return { error: error.message }
    }
  },

  changeUserRole: async (userId: string, role: 'admin' | 'trainer') => {
    try {
      const { profile } = useAuthStore.getState()
      if (!profile || profile.role !== 'admin') {
        return { error: 'Nur Administratoren können Rollen ändern' }
      }

      // Prevent removing admin role from the current admin
      if (userId === profile.id && role === 'trainer') {
        return { error: 'Sie können sich nicht selbst die Admin-Rechte entziehen' }
      }

      const { error } = await supabase
        .from('profiles')
        .update({ role })
        .eq('id', userId)

      if (error) throw error

      // Update local state
      const users = get().users.map(user => 
        user.id === userId ? { ...user, role } : user
      )
      set({ users })

      return {}
    } catch (error: any) {
      return { error: error.message }
    }
  },

  deleteUser: async (userId: string) => {
    try {
      const { profile } = useAuthStore.getState()
      if (!profile || profile.role !== 'admin') {
        return { error: 'Nur Administratoren können Benutzer löschen' }
      }

      // Prevent deleting self
      if (userId === profile.id) {
        return { error: 'Sie können sich nicht selbst löschen' }
      }

      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId)

      if (error) throw error

      // Update local state
      const users = get().users.filter(user => user.id !== userId)
      set({ users })

      return {}
    } catch (error: any) {
      return { error: error.message }
    }
  },

  getPendingUsers: () => get().users.filter(user => user.status === 'pending'),
  getApprovedUsers: () => get().users.filter(user => user.status === 'approved'),
  getRejectedUsers: () => get().users.filter(user => user.status === 'rejected'),
  getAdminEmails: () => get().users.filter(user => user.role === 'admin' && user.status === 'approved').map(user => user.email),
}))
